import { useEffect, useId, useState } from "react";
import { Type, Check, AlertTriangle, Loader2 } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { FONT_RX, POPULAR_FONTS, fontExists, fontStyle } from "../../lib/googleFonts";
import { FontPresets } from "./FontPresets";

function FontField({ id, label, value, onChange, sample, bold }) {
  const [state, setState] = useState("");
  const listId = useId();
  useEffect(() => {
    const n = (value || "").trim();
    if (!n) { setState(""); return undefined; }
    if (!FONT_RX.test(n)) { setState("bad"); return undefined; }
    setState("checking");
    let live = true;
    const t = setTimeout(() => fontExists(n).then((ok) => live && setState(ok ? "ok" : "missing")), 500);
    return () => { live = false; clearTimeout(t); };
  }, [value]);
  const show = state === "ok" ? value : "";
  return (
    <div data-testid={`hero-font-${id}`}>
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        {value && <button type="button" onClick={() => onChange("")} className="text-[11px] font-semibold text-[#F5551E] hover:underline" data-testid={`hero-font-${id}-clear`}>Use default</button>}
      </div>
      <Input list={listId} value={value || ""} onChange={(e) => onChange(e.target.value.replace(/[^A-Za-z0-9 ]/g, "").slice(0, 40))}
        placeholder="Default (site font)" className="mt-1.5 h-9 rounded-lg" data-testid={`hero-font-${id}-input`} />
      <datalist id={listId}>{POPULAR_FONTS.map((f) => <option key={f} value={f} />)}</datalist>
      <p className="text-[11px] mt-1 flex items-center gap-1 min-h-[16px]" data-testid={`hero-font-${id}-status`} data-state={state}>
        {state === "checking" && <><Loader2 size={11} className="animate-spin text-slate-400" /><span className="text-slate-400">Checking Google Fonts…</span></>}
        {state === "ok" && <><Check size={11} className="text-emerald-600" /><span className="text-emerald-700">Found on Google Fonts</span></>}
        {(state === "missing" || state === "bad") && <><AlertTriangle size={11} className="text-amber-600" /><span className="text-amber-700">Not found on Google Fonts — check the spelling (e.g. “Playfair Display”). Visitors would see the default font.</span></>}
        {!state && <span className="text-slate-400">Type any font name from fonts.google.com</span>}
      </p>
      <p className={`mt-1 rounded-lg bg-[#12234A] text-white px-3 py-2 truncate ${bold ? "text-lg font-extrabold" : "text-sm"}`} style={fontStyle(show)} data-testid={`hero-font-${id}-sample`}>{sample}</p>
    </div>
  );
}

export function HeroFonts({ slide, set }) {
  return (
    <div className="sm:col-span-2 rounded-xl border border-slate-200 bg-white p-3" data-testid="hero-slide-fonts">
      <p className="text-sm font-semibold text-[#12234A] flex items-center gap-1.5 mb-2"><Type size={14} className="text-[#F5551E]" /> Fonts for this slide</p>
      <FontPresets slide={slide} set={set} />
      <div className="grid sm:grid-cols-2 gap-3">
        <FontField id="title" label="Headline font" value={slide.title_font} onChange={(v) => set("title_font", v)} bold
          sample={`${slide.title_line1 || "Find your next"} ${slide.title_line2 || "Best friend."}`} />
        <FontField id="text" label="Supporting text font" value={slide.text_font} onChange={(v) => set("text_font", v)}
          sample={slide.subtitle || "Adopt, buy and sell pets across Canada."} />
      </div>
      <p className="text-[11px] text-slate-400 mt-2">The supporting text font is used for the small badge and the subtitle. Fancy or handwritten fonts work best for short headlines.</p>
    </div>
  );
}
